import { DOCUMENT, inject, Service } from '@angular/core';
import { normalizeLanguage, SupportedLanguage } from '../functions/supported-language.function';

/**
 * Tells the language switch what it is running on: one bundle per language under
 * `/<language>/`, or a single-language dev-server.
 *
 * That distinction decides whether switching the language is a full-page
 * redirect to another bundle or nothing at all — the Angular dev-server compiles
 * exactly one locale (see the `i18n-build-config` skill), so there is nowhere to
 * redirect to there, while `nx build` emits all four and nginx serves them side
 * by side, locally (`tools/local-nginx/`) as in production.
 *
 * The signal is the document's `<base href>`, which a localized build fills with
 * `/<language>/` from the locale's `subPath` and the dev-server leaves at `/`.
 * Reading the deployment rather than the environment name is what lets the same
 * `localhost` build be served both ways and behave correctly in each — keyed on
 * the environment, a locally served build would silently refuse to switch.
 */
@Service()
export class LocaleDeploymentService {
  private readonly document = inject(DOCUMENT);

  /** The language this document is served as, or `null` when nothing prefixes it. */
  public get servedLanguage(): SupportedLanguage | null {
    const segments = this.baseSegments;
    return segments.length ? normalizeLanguage(segments[segments.length - 1]) : null;
  }

  /** Whether a sibling bundle exists to redirect to. */
  public get isLanguagePrefixed(): boolean {
    return this.servedLanguage !== null;
  }

  /**
   * The absolute URL of `path` in `language`, keeping whatever the app is
   * deployed under above the language segment.
   *
   * @param {string} language - Target language, i.e. the sibling bundle to leave to.
   * @param {string} path - Route inside that bundle, without a leading slash; the
   *   language root when empty.
   * @returns {string} The absolute URL to redirect to.
   */
  public urlForLanguage(language: string, path = ''): string {
    const route = path.replace(/^\//, '');
    return [this.deploymentRoot, language, route].filter(Boolean).join('/');
  }

  /**
   * Origin plus the path the app is deployed under, with the language segment
   * and any trailing slash removed — `http://localhost:5320` for a plain host,
   * `https://example.ch/lexplus` for a sub-path deployment.
   */
  private get deploymentRoot(): string {
    const segments = this.baseSegments;
    const aboveLanguage = this.servedLanguage ? segments.slice(0, -1) : segments;
    return [this.baseUrl.origin, ...aboveLanguage].join('/');
  }

  private get baseSegments(): string[] {
    return this.baseUrl.pathname.split('/').filter(Boolean);
  }

  private get baseUrl(): URL {
    return new URL(this.document.baseURI);
  }
}
